/**
 * State 02 — Sign in: Verify code (new device).
 *
 * The user has already submitted email + password in state 01 and
 * Clerk responded with needs_first_factor. Start card called
 * `signIn.prepareFirstFactor({ strategy: "email_code" })`, so a
 * 6-digit code is in the inbox.
 *
 * On submit: `signIn.attemptFirstFactor({ strategy: "email_code", code })`
 *   - status complete → setActive + close
 *   - else → surface error
 *
 * Mockup state 02 includes the "new device" advisory banner with a
 * warning triangle. Copy verbatim.
 */

"use client";

import { useState } from "react";

import { useSignIn } from "@clerk/nextjs";

import { activateSession } from "./sessionActivation";

import { IconWarningTriangle } from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import { IdentityRow } from "./IdentityRow";
import { OtpInput } from "./OtpInput";
import { useResendCountdown } from "./useResendCountdown";
import {
  AuthFlowState,
  AuthStep,
  readClerkError,
} from "./useAuthFlow";
import { trackSignupCompleted } from "@/lib/analytics";

type Props = {
  flow: AuthFlowState;
  setIdentifier: (v: string) => void;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  markResend: () => void;
  goTo: (s: AuthStep) => void;
  onAuthenticated: () => void;
};

export function SignInVerifyCodeCard({
  flow,
  setError,
  setSubmitting,
  markResend,
  goTo,
  onAuthenticated,
}: Props) {
  const { signIn, setActive, isLoaded } = useSignIn();
  const [code, setCode] = useState("");
  const resend = useResendCountdown(flow.lastResendAt);

  async function attempt(value: string) {
    if (!isLoaded || !signIn || !setActive) return;
    if (value.length !== 6) return;
    setError(null);
    setSubmitting(true);
    try {
      // Dispatch against the same factor tier the user was prepared
      // for. "second" = new-device MFA; everything else = first-factor
      // email-code (state 05 alternative method, or null fallback).
      const result =
        flow.verifyMode === "second"
          ? await signIn.attemptSecondFactor({
              strategy: "email_code",
              code: value,
            })
          : await signIn.attemptFirstFactor({
              strategy: "email_code",
              code: value,
            });
      if (result.status === "complete" && result.createdSessionId) {
        // 2026-08-29: bounded activation (sessionActivation.ts).
        await activateSession({
          setActive,
          sessionId: result.createdSessionId,
        });
        // C21: through the sign-up gate. Attribution (negotiate vs
        // elsewhere) comes from the marker written when the gate was raised.
        trackSignupCompleted({ method: "sign_in" });
        onAuthenticated();
        return;
      }
      setError("Could not verify the code. Try again.");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (!isLoaded || !signIn) return;
    if (resend.disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      if (flow.verifyMode === "second") {
        const emailSecond = signIn.supportedSecondFactors?.find(
          (f): f is typeof f & { emailAddressId: string } =>
            f.strategy === "email_code",
        );
        if (!emailSecond) return;
        await signIn.prepareSecondFactor({
          strategy: "email_code",
          emailAddressId: emailSecond.emailAddressId,
        });
        markResend();
        return;
      }
      const emailFactor = signIn.supportedFirstFactors?.find(
        (f): f is typeof f & { emailAddressId: string } =>
          f.strategy === "email_code",
      );
      if (!emailFactor) return;
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailFactor.emailAddressId,
      });
      markResend();
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void attempt(code);
      }}
    >
      <div className="mio-auth-card-header">
        <h1>Enter verification code</h1>
        <p>We sent a 6-digit code to your email.</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <IdentityRow
        value={flow.identifier}
        onEdit={() => goTo("signIn.start")}
      />

      <div className="mio-auth-otp-wrap">
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={attempt}
          disabled={flow.submitting}
        />
        <div className="mio-auth-resend">
          Didn&rsquo;t receive a code?{" "}
          <button
            type="button"
            onClick={() => void onResend()}
            disabled={resend.disabled || flow.submitting}
          >
            Resend
          </button>
          {resend.disabled ? (
            <span className="mio-auth-countdown">{resend.label}</span>
          ) : null}
        </div>
      </div>

      {flow.verifyMode === "second" ? (
        <div className="mio-auth-warning" role="note">
          <IconWarningTriangle />
          <div>
            <strong>You&rsquo;re signing in from a new device.</strong>{" "}
            If this wasn&rsquo;t you, cancel and reset your password.
          </div>
        </div>
      ) : null}

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={flow.submitting || code.length !== 6}
      >
        {flow.submitting ? (
          <>
            Verifying
            <EllipsisDots />
          </>
        ) : (
          "Continue"
        )}
      </button>
    </form>
  );
}
